// Global trigger for the shared date-event edit/add dialog. Mounted
// once at the app root (in App.tsx) so any view can request it: the
// Settings → Date events grid uses it for its pencil + "Add" buttons,
// and the calendar's double-click-on-band / on-empty-cell / on-day-
// label gestures all use it for inline editing without leaving the
// Port Calendar.

import { createContext, createSignal, useContext, type JSX } from "solid-js";
import type { DateEvent } from "../../../../backend/shared";

export type EditTarget =
  | { mode: "edit"; event: DateEvent }
  | { mode: "new"; preset: Partial<Omit<DateEvent, "id">> }
  | null;

interface DateEventDialogCtx {
  target: () => EditTarget;
  openEdit: (event: DateEvent) => void;
  openNew: (preset?: Partial<Omit<DateEvent, "id">>) => void;
  close: () => void;
}

const Ctx = createContext<DateEventDialogCtx | null>(null);

export function DateEventDialogProvider(props: { children: JSX.Element }) {
  const [target, setTarget] = createSignal<EditTarget>(null);
  return (
    <Ctx.Provider value={{
      target,
      openEdit: (event) => setTarget({ mode: "edit", event }),
      openNew: (preset) => setTarget({ mode: "new", preset: preset ?? {} }),
      close: () => setTarget(null),
    }}>
      {props.children}
    </Ctx.Provider>
  );
}

export function useDateEventDialog(): DateEventDialogCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("DateEventDialogProvider missing");
  return v;
}
